import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CardModule, GridModule, ButtonModule, SpinnerModule, BadgeModule, AlertModule, ModalModule, ButtonCloseDirective } from '@coreui/angular-pro';
import { TranslatePipe, TranslationModule } from 'i18n';
import { Logger } from 'shared';

// Admin matrix editor (v4.0 ch.22/23/30). Everything that drives the calculation is
// editable here without a deployment: the two point formulas, the six leadership
// ranks, the two Pack Hero levels and the 70/30 pool split.
//
// Note what the columns mean per category — they are not uniform. For the point
// formulas the numbers live in `params` (divisor, points per group); for the ranks
// `threshold` is a member count and `points` is a MONTHLY USD amount, not points.
// The category description says which, and is rendered above each table.
// Super-admin only (route + backend gated).
@Component({
  selector: 'app-wpcp-matrix-page',
  standalone: true,
  imports: [CommonModule, FormsModule, CardModule, GridModule, ButtonModule, SpinnerModule, BadgeModule, AlertModule, ModalModule, ButtonCloseDirective, TranslationModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './wpcp-matrix-page.component.html'
})
export class WpcpMatrixPageComponent {
  private http = inject(HttpClient);
  private destroyRef = inject(DestroyRef);
  private logger = new Logger('WpcpMatrixPageComponent');

  categories = signal<any[]>([]);
  rules = signal<any[]>([]);
  version = signal<string | null>(null);
  loading = signal(true);
  message = signal<{ type: string; text: string } | null>(null);

  ratio = { community_ratio: 0.7, legacy_ratio: 0.3, reserve: 1.0 };

  versions = signal<any[]>([]);
  publishForm = { version_number: '', valid_from: '', notes: '' };

  versionDetail = signal<any | null>(null);
  versionModalVisible = signal(false);

  constructor() {
    this.loadMatrix();
    this.loadConfig();
    this.loadVersions();
  }

  private loadVersions() {
    this.http.get<any>('/wpcp/admin/matrix/versions').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (r) => this.versions.set(r.versions || []),
      error: (e) => this.logger.warn('versions load failed', e)
    });
  }

  viewVersion(v: any) {
    this.http.get<any>(`/wpcp/admin/matrix/versions/${v.id}`).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (r) => { this.versionDetail.set(r); this.versionModalVisible.set(true); },
      error: (e) => this.flash('danger', e?.error?.error || 'Load failed')
    });
  }

  publishVersion() {
    if (!this.publishForm.version_number.trim()) { this.flash('danger', 'Version number required'); return; }
    this.http.post<any>('/wpcp/admin/matrix/versions', this.publishForm).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => { this.flash('success', 'Version published'); this.publishForm = { version_number: '', valid_from: '', notes: '' }; this.loadVersions(); this.loadMatrix(); },
      error: (e) => this.flash('danger', e?.error?.error || 'Publish failed')
    });
  }

  private loadMatrix() {
    this.loading.set(true);
    this.http.get<any>('/wpcp/admin/matrix').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (m) => { this.categories.set(m.categories || []); this.rules.set(m.rules || []); this.version.set(m.version); this.loading.set(false); },
      error: (e) => { this.logger.error('matrix load failed', e); this.loading.set(false); }
    });
  }

  private loadConfig() {
    this.http.get<any>('/wpcp/admin/pool/config').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (c) => { this.ratio = { community_ratio: c.community_ratio, legacy_ratio: c.legacy_ratio, reserve: c.reserve }; },
      error: (e) => this.logger.warn('config load failed', e)
    });
  }

  rulesFor(key: string) {
    return this.rules().filter((r) => r.category_key === key);
  }

  /** The formula dials of a v4.0 auto rule, or [] for rules that have none. */
  paramKeys(r: any): string[] {
    return r?.params ? Object.keys(r.params) : [];
  }

  paramLabel(key: string): string {
    switch (key) {
      case 'divisor': return 'per';
      case 'points_per_group': return 'points';
      case 'min_directs_per_builder': return 'min. directs';
      default: return key;
    }
  }

  saveRule(r: any) {
    const body: any = { points: r.points, threshold: r.threshold, min_points: r.min_points, max_points: r.max_points, active: r.active };
    // Send the dials as numbers — the backend casts them to integers, and a string
    // would silently become 0 and switch the formula off.
    if (r.params) {
      body.params = {};
      Object.keys(r.params).forEach((k) => (body.params[k] = Number(r.params[k])));
    }
    this.http.patch<any>(`/wpcp/admin/rules/${r.code}`, body).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.flash('success', `${r.code} saved`),
      error: (e) => this.flash('danger', e?.error?.error || 'Save failed')
    });
  }

  saveRatio() {
    this.http.patch<any>('/wpcp/admin/pool/config', this.ratio).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (c) => { this.ratio = { community_ratio: c.community_ratio, legacy_ratio: c.legacy_ratio, reserve: c.reserve }; this.flash('success', 'Split saved'); },
      error: (e) => this.flash('danger', e?.error?.error || 'Save failed')
    });
  }

  private flash(type: string, text: string) {
    this.message.set({ type, text });
    setTimeout(() => this.message.set(null), 4000);
  }
}
